import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Printer, Mail, LoaderCircle, Save, FolderOpen } from "lucide-react";
import { Logo } from "../components/Logo";
import { SiteQr } from "../components/QrCode";
import { CONTACT_INFO } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TEXT = {
  it: { quote: "Preventivo", to: "Spett.le", desc: "Descrizione lavori", amount: "Importo", subtotal: "Subtotale", vat: "IVA 22%", total: "TOTALE", notes: "Note", sign: "Firma per accettazione", city: "Milano", qr: "Inquadra e visita il sito" },
  en: { quote: "Quote", to: "To", desc: "Work description", amount: "Amount", subtotal: "Subtotal", vat: "VAT 22%", total: "TOTAL", notes: "Notes", sign: "Signature for acceptance", city: "Milan", qr: "Scan to visit our website" },
};

const NOTE_DEFAULTS = {
  it: "Preventivo valido 30 giorni. Sopralluogo e progettazione preliminare inclusi.",
  en: "Quote valid for 30 days. Site survey and preliminary design included.",
};

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-brand";

export default function Preventivo() {
  const [cliente, setCliente] = useState("");
  const [clienteIndirizzo, setClienteIndirizzo] = useState("");
  const [clienteEmail, setClienteEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [numero, setNumero] = useState(`PREV-${new Date().getFullYear()}-001`);
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [conIva, setConIva] = useState(true);
  const [lang, setLang] = useState("it");
  const [note, setNote] = useState(NOTE_DEFAULTS.it);
  const [items, setItems] = useState([{ descrizione: "", prezzo: "" }]);
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);

  const L = DOC_TEXT[lang];
  const euro = new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-IE", { style: "currency", currency: "EUR" });

  const onLangChange = (l) => {
    setLang(l);
    setNote((prev) => (prev === NOTE_DEFAULTS.it || prev === NOTE_DEFAULTS.en ? NOTE_DEFAULTS[l] : prev));
  };

  const subtotale = useMemo(
    () => items.reduce((s, it) => s + (parseFloat(it.prezzo) || 0), 0),
    [items]
  );
  const iva = conIva ? subtotale * 0.22 : 0;
  const totale = subtotale + iva;

  const token = localStorage.getItem("te_admin_token");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setSaved(data))
      .catch(() => {});
  }, [token]);

  if (!token) return <Navigate to="/admin" replace />;

  const currentPayload = () => ({
    cliente,
    cliente_indirizzo: clienteIndirizzo || null,
    cliente_email: clienteEmail || null,
    numero,
    data,
    con_iva: conIva,
    note: note || null,
    lang,
    items: items
      .filter((it) => it.descrizione.trim() && parseFloat(it.prezzo) > 0)
      .map((it) => ({ descrizione: it.descrizione.trim(), prezzo: parseFloat(it.prezzo) })),
  });

  const refreshSaved = async () => {
    const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
    setSaved(data);
  };

  const saveQuote = async () => {
    const payload = currentPayload();
    if (!cliente.trim() || payload.items.length === 0) {
      toast.error("Inserisci il cliente e almeno una voce con prezzo");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/quotes`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preventivo salvato", { description: `Lo ritrovi come ${numero} nell'elenco sotto.` });
      refreshSaved();
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const loadQuote = (q) => {
    setCliente(q.cliente);
    setClienteIndirizzo(q.cliente_indirizzo || "");
    setClienteEmail(q.cliente_email || "");
    setNumero(q.numero);
    setData(q.data);
    setConIva(q.con_iva);
    setNote(q.note || "");
    setItems(q.items.map((i) => ({ descrizione: i.descrizione, prezzo: String(i.prezzo) })));
    setLang(q.lang || "it");
    toast.success("Preventivo caricato", { description: "Puoi modificarlo, ristamparlo o reinviarlo." });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteQuote = async (id) => {
    await axios.delete(`${API}/quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setSaved((prev) => prev.filter((q) => q.id !== id));
    toast.success("Preventivo eliminato");
  };

  const setItem = (i, key, value) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));
  };

  const sendEmail = async () => {
    if (!cliente.trim() || !clienteEmail.trim()) {
      toast.error("Inserisci nome ed email del cliente");
      return;
    }
    const validItems = items.filter((it) => it.descrizione.trim() && parseFloat(it.prezzo) > 0);
    if (validItems.length === 0) {
      toast.error("Aggiungi almeno una voce con descrizione e prezzo");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/quotes/send`,
        {
          cliente,
          cliente_email: clienteEmail,
          numero,
          data,
          con_iva: conIva,
          note: note || null,
          lang,
          items: validItems.map((it) => ({ descrizione: it.descrizione.trim(), prezzo: parseFloat(it.prezzo) })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Preventivo inviato!", { description: `Email consegnata a ${clienteEmail}` });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Invio non riuscito", { description: typeof detail === "string" ? detail : "Controlla i dati e riprova." });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface2/60 font-body text-slate-900" data-testid="preventivo-page">
      <div className="no-print mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-6">
        <Link to="/admin" className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-brand" data-testid="quote-back-admin">
          <ArrowLeft size={16} /> Torna all'area riservata
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={saveQuote}
            disabled={saving}
            className="flex items-center gap-2 rounded-full border border-line bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-brand hover:text-brand disabled:opacity-60"
            data-testid="quote-save-button"
          >
            {saving ? <LoaderCircle size={16} className="animate-spin" /> : <Save size={16} />}
            Salva
          </button>
          <div className="flex items-center rounded-full border border-line bg-white p-1" data-testid="quote-lang-toggle">
            {["it", "en"].map((l) => (
              <button
                key={l}
                onClick={() => onLangChange(l)}
                data-testid={`quote-lang-${l}`}
                className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                  lang === l ? "bg-brand text-white" : "text-slate-500 hover:text-brand"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={sendEmail}
            disabled={sendingEmail}
            className="flex items-center gap-2 rounded-full border border-brand bg-white px-6 py-3 text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-white disabled:opacity-60"
            data-testid="quote-send-email-button"
          >
            {sendingEmail ? <LoaderCircle size={16} className="animate-spin" /> : <Mail size={16} />}
            {sendingEmail ? "Invio in corso..." : "Invia via email"}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-amber"
            data-testid="quote-print-button"
          >
            <Printer size={16} /> Stampa / Salva PDF
          </button>
        </div>
      </div>

      {saved.length > 0 && (
        <div className="no-print mx-auto max-w-7xl px-5 pb-8" data-testid="saved-quotes-section">
          <div className="rounded-2xl border border-line bg-white p-6 shadow-sm">
            <h2 className="font-heading text-base font-bold text-slate-900">Preventivi salvati ({saved.length})</h2>
            <div className="mt-4 space-y-2" data-testid="saved-quotes-list">
              {saved.map((q) => {
                const tot = q.items.reduce((s, i) => s + i.prezzo, 0) * (q.con_iva ? 1.22 : 1);
                return (
                  <div key={q.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line px-4 py-3" data-testid={`saved-quote-${q.id}`}>
                    <div className="text-sm">
                      <span className="font-heading font-semibold text-slate-900">{q.numero}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="text-slate-600">{q.cliente}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="font-semibold text-brand">{euro.format(tot)}</span>
                      <span className="ml-2 rounded-full bg-surface2 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{(q.lang || "it").toUpperCase()}</span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => loadQuote(q)} className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand" data-testid={`quote-load-${q.id}`}>
                        <FolderOpen size={13} /> Apri
                      </button>
                      <button onClick={() => deleteQuote(q.id)} className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-red-400 hover:text-red-500" data-testid={`quote-delete-${q.id}`}>
                        <Trash2 size={13} /> Elimina
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-16 lg:grid-cols-2 print:block">
        <div className="no-print rounded-2xl border border-line bg-white p-7 shadow-sm sm:p-8" data-testid="quote-form">
          <h1 className="font-heading text-xl font-bold">Crea un preventivo</h1>
          <p className="mt-1 text-sm text-slate-500">Compila i campi: il documento a destra si aggiorna da solo con i dati TecnoEdil.</p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="q-cliente">Cliente *</label>
              <input id="q-cliente" value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Sig. Mario Rossi" className={inputCls} data-testid="quote-client-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="q-indirizzo">Indirizzo cliente</label>
              <input id="q-indirizzo" value={clienteIndirizzo} onChange={(e) => setClienteIndirizzo(e.target.value)} placeholder="Via Roma 1, Milano" className={inputCls} data-testid="quote-client-address-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="q-email-cliente">Email del cliente (per l'invio diretto)</label>
              <input id="q-email-cliente" type="email" value={clienteEmail} onChange={(e) => setClienteEmail(e.target.value)} placeholder="cliente@esempio.it" className={inputCls} data-testid="quote-client-email-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="q-numero">N. preventivo</label>
              <input id="q-numero" value={numero} onChange={(e) => setNumero(e.target.value)} className={inputCls} data-testid="quote-number-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="q-data">Data</label>
              <input id="q-data" type="date" value={data} onChange={(e) => setData(e.target.value)} className={inputCls} data-testid="quote-date-input" />
            </div>
          </div>

          <p className="mb-2 mt-7 block text-xs font-semibold uppercase tracking-wider text-slate-500">Voci di lavoro</p>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={it.descrizione}
                  onChange={(e) => setItem(i, "descrizione", e.target.value)}
                  placeholder="Es. Rifacimento bagno completo"
                  className={inputCls}
                  data-testid={`quote-item-desc-${i}`}
                />
                <input
                  value={it.prezzo}
                  onChange={(e) => setItem(i, "prezzo", e.target.value.replace(/[^0-9.,]/g, ""))}
                  placeholder="€"
                  inputMode="decimal"
                  className={`${inputCls} w-28 shrink-0 text-right`}
                  data-testid={`quote-item-price-${i}`}
                />
                <button
                  onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                  disabled={items.length === 1}
                  className="shrink-0 rounded-xl border border-line px-3 text-slate-400 transition-colors hover:border-red-400 hover:text-red-500 disabled:opacity-30"
                  aria-label="Rimuovi voce"
                  data-testid={`quote-item-remove-${i}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setItems((prev) => [...prev, { descrizione: "", prezzo: "" }])}
            className="mt-3 flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand"
            data-testid="quote-add-item"
          >
            <Plus size={14} /> Aggiungi voce
          </button>

          <label className="mt-6 flex items-center gap-2.5 text-sm text-slate-700" data-testid="quote-iva-toggle">
            <input type="checkbox" checked={conIva} onChange={(e) => setConIva(e.target.checked)} className="h-4 w-4 accent-brand" />
            Applica IVA 22%
          </label>

          <label className="mb-1.5 mt-6 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="q-note">Note</label>
          <textarea id="q-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} resize-none`} data-testid="quote-note-input" />
        </div>

        <div className="rounded-2xl border border-line bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10" id="preventivo-doc" data-testid="quote-preview">
          <div className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-6">
            <Logo className="h-20" />
            <div className="text-right text-xs leading-relaxed text-slate-600">
              <p className="font-heading text-sm font-bold text-slate-900">TecnoEdil.EA S.R.L.S</p>
              <p>{CONTACT_INFO.address}</p>
              <p>{CONTACT_INFO.piva}</p>
              <p>Tel. {CONTACT_INFO.phone}</p>
              <p>{CONTACT_INFO.email}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{L.quote}</p>
              <h2 className="font-heading text-2xl font-extrabold text-slate-900" data-testid="quote-preview-number">N. {numero}</h2>
            </div>
            <p className="text-sm text-slate-600">
              {L.city}, {new Date(data).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>

          <div className="mt-6 rounded-xl bg-surface2/70 p-5 text-sm">
            <p className="text-xs uppercase tracking-wider text-slate-400">{L.to}</p>
            <p className="mt-1 font-heading text-base font-semibold text-slate-900" data-testid="quote-preview-client">
              {cliente || "—"}
            </p>
            {clienteIndirizzo && <p className="text-slate-600">{clienteIndirizzo}</p>}
          </div>

          <table className="mt-8 w-full text-sm">
            <thead>
              <tr className="border-b border-slate-900 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-4 font-semibold">{L.desc}</th>
                <th className="py-2 text-right font-semibold">{L.amount}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-line">
                  <td className="py-3 pr-4 text-slate-700">{it.descrizione || "—"}</td>
                  <td className="py-3 text-right font-semibold text-slate-900">
                    {parseFloat(it.prezzo) ? euro.format(parseFloat(it.prezzo)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 flex justify-end">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>{L.subtotal}</span>
                <span data-testid="quote-preview-subtotal">{euro.format(subtotale)}</span>
              </div>
              {conIva && (
                <div className="flex justify-between text-slate-600">
                  <span>{L.vat}</span>
                  <span>{euro.format(iva)}</span>
                </div>
              )}
              <div className="flex justify-between border-t-2 border-slate-900 pt-2 font-heading text-lg font-extrabold text-slate-900">
                <span>{L.total}</span>
                <span data-testid="quote-preview-total">{euro.format(totale)}</span>
              </div>
            </div>
          </div>

          {note && (
            <div className="mt-8 rounded-xl border border-line p-4 text-xs leading-relaxed text-slate-600">
              <p className="mb-1 font-semibold uppercase tracking-wider text-slate-400">{L.notes}</p>
              {note}
            </div>
          )}

          <div className="mt-12 flex items-end justify-between gap-6 text-xs text-slate-500">
            <p className="max-w-[220px]">
              TecnoEdil.EA S.R.L.S · {CONTACT_INFO.piva} · {CONTACT_INFO.phone} · {CONTACT_INFO.email}
            </p>
            <div className="text-center" data-testid="quote-qr">
              <SiteQr size={72} />
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{L.qr}</p>
            </div>
            <p className="text-right">
              {L.sign}
              <span className="mt-6 block w-44 border-b border-slate-400" />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
